import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import format from "date-fns/format";
import { Entity } from "models/entity";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";

import CustomCard from "components/CustomCard";
import { SKSpinner } from "components/customElements/skSpinner";
import EmptyState from "components/EmptyState";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import { baseFetcher } from "utils/baseFetcher";

import { estimatesUrl, isDataEmpty } from "../utils";

interface NextEarningProps {
  entity: Entity;
}

const NextEarning = ({ entity }: NextEarningProps) => {
  const { data: estimatesData, isValidating } = useApi(
    estimatesUrl(entity.id),
    baseFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    next_earn,
    ["fest_actsales-quarterly"]: festActsalesQuarterly,
    ["fest_actebit-quarterly"]: festActebitQuarterly,
    ["fest_actebitda-quarterly"]: festActebitdaQuarterly,
    ["fest_acteps_rpt-quarterly"]: festActepsRptQuarterly,
    ["fest_acteps-quarterly"]: festActepsQuarterly,
    fest_estsales_rp_fq0,
    fest_estebitda_rp_fq0,
    fest_estebit_rp_fq0,
    fest_esteps_gap_mean_rp_fq0,
    fest_esteps_rp_fq0,
  } = estimatesData || {};

  if (!isValidating && isDataEmpty(estimatesData))
    return (
      <CustomCard
        className={twMerge(
          "px-[15px] py-[15px] shadow-none md:shadow-md mb-0 pb-0 md:pb-[15px] md:mb-[30px]"
        )}
      >
        <h5 className="text-[16px] font-medium">Next Earnings</h5>
        <EmptyState
          type="data"
          className="pt-[50px] md:pt-[50px] pb-[50px] md:pb-[50px]"
        />
      </CustomCard>
    );

  return (
    <>
      <CustomCard
        className={twMerge(
          "px-[15px] py-[15px] shadow-none md:shadow-md mb-0 pb-0 md:pb-[15px] md:mb-[30px]",
          "shadow-[0_2px_15px_rgba(0,0,0,0.15)]"
        )}
      >
        <div className="flex">
          <h5 className="text-[16px] font-medium flex-1">Next Earnings</h5>
          {isValidating ? (
            <SKSpinner showLoading={false} />
          ) : (
            next_earn?.date && (
              <OverlayTrigger
                overlay={
                  next_earn.status?.toLowerCase() === "confirmed" ? (
                    <Tooltip>Confirmed Earnings Date</Tooltip>
                  ) : next_earn.when ? (
                    <></>
                  ) : (
                    <Tooltip>Estimated Earnings Date</Tooltip>
                  )
                }
              >
                <div className="text-[13px]">
                  {format(new Date(next_earn.date), "dd MMM yyyy")}{" "}
                  {next_earn.status?.toLowerCase() === "confirmed" ? (
                    <div className="inline-block">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-bullish-green"
                      />
                    </div>
                  ) : next_earn.when ? (
                    `(${next_earn.when})`
                  ) : (
                    <div className="inline-block">
                      <FontAwesomeIcon icon={faQuestionCircle} />
                    </div>
                  )}
                </div>
              </OverlayTrigger>
            )
          )}
        </div>
        {isValidating ? (
          <div className="w-full flex justify-center">
            <SKSpinner />
          </div>
        ) : (
          estimatesData && (
            <div className="">
              <table
                className={twMerge("table-auto w-full mt-[15px] text-[12px]")}
              >
                <tbody>
                  <tr>
                    <td className="w-[40%]"></td>
                    <td className="w-[30%] text-right font-bold">NEXT (E)</td>
                    <td className="w-[30%] text-right font-bold">GROWTH</td>
                  </tr>
                  <tr className="border-0 border-b border-gray-300">
                    <td className="py-[9px]">Revenues</td>
                    <td className="whitespace-nowrap text-right font-bold">
                      {typeof fest_estsales_rp_fq0?.value != "undefined" ? (
                        <>
                          {fest_estsales_rp_fq0.currency}{" "}
                          {numeral(
                            fest_estsales_rp_fq0?.value * 1000000
                          ).format("0,0.[00]a")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={twMerge(
                        "text-right",
                        "font-bold",
                        fest_estsales_rp_fq0?.value <
                          festActsalesQuarterly?.value
                          ? "text-bearish-red"
                          : "text-bullish-green"
                      )}
                    >
                      {typeof fest_estsales_rp_fq0?.value != "undefined" ? (
                        <>
                          {numeral(
                            (fest_estsales_rp_fq0?.value -
                              festActsalesQuarterly?.value) /
                              festActsalesQuarterly?.value
                          ).format("0.00%")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr className="border-0 border-b border-gray-300">
                    <td className="py-[9px]">EBITDA</td>
                    <td className="whitespace-nowrap text-right font-bold">
                      {typeof fest_estebitda_rp_fq0?.value != "undefined" ? (
                        <>
                          {fest_estebitda_rp_fq0.currency}{" "}
                          {numeral(
                            fest_estebitda_rp_fq0?.value * 1000000
                          ).format("0,0.[00]a")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={twMerge(
                        "text-right",
                        "font-bold",
                        fest_estebitda_rp_fq0?.value <
                          festActebitdaQuarterly?.value
                          ? "text-bearish-red"
                          : "text-bullish-green"
                      )}
                    >
                      {typeof fest_estebitda_rp_fq0?.value != "undefined" ? (
                        <>
                          {numeral(
                            (fest_estebitda_rp_fq0?.value -
                              festActebitdaQuarterly?.value) /
                              festActebitdaQuarterly?.value
                          ).format("0.00%")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr className="border-0 border-b border-gray-300">
                    <td className="py-[9px]">EBIT</td>
                    <td className="whitespace-nowrap text-right  font-bold">
                      {typeof fest_estebit_rp_fq0?.value != "undefined" ? (
                        <>
                          {fest_estebit_rp_fq0.currency}{" "}
                          {numeral(fest_estebit_rp_fq0?.value * 1000000).format(
                            "0,0.[00]a"
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={twMerge(
                        "text-right",
                        "font-bold",
                        fest_estebit_rp_fq0?.value < festActebitQuarterly?.value
                          ? "text-bearish-red"
                          : "text-bullish-green"
                      )}
                    >
                      {typeof fest_estebit_rp_fq0?.value != "undefined" ? (
                        <>
                          {numeral(
                            (fest_estebit_rp_fq0?.value -
                              festActebitQuarterly?.value) /
                              festActebitQuarterly?.value
                          ).format("0.00%")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr className="border-0 border-b border-gray-300">
                    <td className="py-[9px]">EPS GAAP</td>
                    <td className="whitespace-nowrap text-right  font-bold">
                      {typeof fest_esteps_gap_mean_rp_fq0?.value !=
                      "undefined" ? (
                        <>
                          {numeral(fest_esteps_gap_mean_rp_fq0?.value).format(
                            "0,0.[00]"
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={twMerge(
                        "text-right",
                        "font-bold",
                        fest_esteps_gap_mean_rp_fq0?.value <
                          festActepsRptQuarterly?.value
                          ? "text-bearish-red"
                          : "text-bullish-green"
                      )}
                    >
                      {typeof fest_esteps_gap_mean_rp_fq0?.value !=
                      "undefined" ? (
                        <>
                          {numeral(
                            (fest_esteps_gap_mean_rp_fq0?.value -
                              festActepsRptQuarterly?.value) /
                              festActepsRptQuarterly?.value
                          ).format("0.00%")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr className="">
                    <td className="py-[9px]">EPS</td>
                    <td className="whitespace-nowrap text-right  font-bold">
                      {typeof fest_esteps_rp_fq0?.value != "undefined" ? (
                        <>
                          {numeral(fest_esteps_rp_fq0?.value).format(
                            "0,0.[00]"
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={twMerge(
                        "text-right",
                        "font-bold",
                        fest_esteps_rp_fq0?.value < festActepsQuarterly?.value
                          ? "text-bearish-red"
                          : "text-bullish-green"
                      )}
                    >
                      {typeof fest_esteps_rp_fq0?.value != "undefined" ? (
                        <>
                          {numeral(
                            (fest_esteps_rp_fq0?.value -
                              festActepsQuarterly?.value) /
                              festActepsQuarterly?.value
                          ).format("0.00%")}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )
        )}
      </CustomCard>
    </>
  );
};

export default NextEarning;
